// LanguageSwitcher Revyo — dropdown drapeaux pour changer de langue.
//
// P1 : ce composant est CRÉÉ mais NON INTÉGRÉ dans Navbar/Footer. Il sera
// branché en phase 2 quand le reste de l'app sera multilingue (sinon on
// donne à l'utilisateur l'illusion qu'il peut changer de langue, alors que
// seul le Footer est traduit en P1).
//
// Style aligné Aurora : fond paper #EDEAE3, accent orange #E8502A, ink #1A1A1A.
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, LANG_LABELS } from '../../i18n/config';
import { writeLanguageChoice } from '../../i18n/detectLang';
import { API_BASE } from '../../config/api';
import styles from './LanguageSwitcher.module.css';

/**
 * i18n Phase 6 — persistance backend (best-effort).
 *
 * Quand l'user a un JWT en localStorage, on push la nouvelle lang sur le
 * backend via PATCH /api/account/preferred-lang. Utilisée pour :
 *  - weekly-report (lundi 9h) → email dans la langue de l'user
 *  - insightsService → prompt Claude dans la langue de l'user
 *
 * Comportement non bloquant :
 *  - Si pas de token (user pas connecté) → skip silencieusement
 *  - Si fetch échoue (offline, 500) → catch silencieux, l'UI a déjà changé
 *  - Si lang refusée par le backend (400 invalid_lang) → idem, on n'a pas
 *    cassé le UX local. La whitelist côté config.js empêche normalement ce cas.
 */
function persistPreferredLang(lang) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return; // user pas connecté → pas de persistance backend
    fetch(`${API_BASE}/api/account/preferred-lang`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ lang }),
    }).catch(() => {
      // Silent fail : on ne bloque jamais l'UX local pour une persistance
      // best-effort. Le prochain login refera un read de preferred_lang.
    });
  } catch {
    // Garde-fou : localStorage peut throw en mode private / iframe sandbox
  }
}

export default function LanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleChange = (lang) => {
    // writeLanguageChoice valide la whitelist côté config.js, donc pas de
    // risque qu'un lang attaquant passe au changeLanguage.
    writeLanguageChoice(lang);
    i18n.changeLanguage(lang);
    // i18n P6 : persistance backend best-effort (silencieuse si pas connecté).
    persistPreferredLang(lang);
    setOpen(false);
  };

  // Fallback robuste si i18n.language sort de la whitelist (ex: 'cimode' en debug)
  const currentKey = LANG_LABELS[i18n.language] ? i18n.language : 'fr';
  const current = LANG_LABELS[currentKey];

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change language"
      >
        <span className={styles.flag} aria-hidden="true">{current.flag}</span>
        {!compact && <span className={styles.native}>{current.native}</span>}
        <span className={styles.caret} aria-hidden="true">▾</span>
      </button>
      {open && (
        <ul className={styles.menu} role="listbox">
          {SUPPORTED_LANGS.map((lng) => {
            const meta = LANG_LABELS[lng];
            const isActive = lng === currentKey;
            return (
              <li key={lng} role="option" aria-selected={isActive}>
                <button
                  type="button"
                  className={`${styles.item} ${isActive ? styles.active : ''}`}
                  onClick={() => handleChange(lng)}
                  lang={lng}
                >
                  <span className={styles.flag} aria-hidden="true">{meta.flag}</span>
                  <span className={styles.native}>{meta.native}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
